/**
 * useClientCustomLabels
 *
 * A custom hook for getting brand-specific custom labels from site copy
 */

import { useMemo } from 'react';
import _get from 'lodash/get';
import { getLocalData } from 'utils/localDataStore';

/**
 * Hook to get a specific custom label for the current brand
 * @param {Object} siteCopy - The site copy object containing clientCustomLabels
 * @param {string} labelKey - The key for the label to retrieve
 * @param {string} fallback - Fallback value if no custom label is found
 * @returns {string} The custom label or fallback value
 */
const useClientCustomLabels = (siteCopy, labelKey, fallback = '') => {
  const brand = getLocalData('brand')?.toLowerCase();

  const customLabel = useMemo(() => {
    const clientCustomLabels = _get(
      siteCopy,
      'pageCopy.clientCustomLabels',
      {},
    );
    return clientCustomLabels[brand]?.[labelKey] || fallback;
  }, [siteCopy, brand, labelKey, fallback]);

  return customLabel;
};

/**
 * Helper function to capitalize the first word of a sentence
 * @param {string} text - The text to capitalize
 * @returns {string} Text with first word capitalized
 */
const capitalizeFirstWord = text => {
  if (!text || typeof text !== 'string') return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Helper function to get a label with fallback hierarchy:
 * 1. Client custom labels (brand-specific) from nested section
 * 2. Site copy labels (general) from section
 * 3. Default fallback value
 *
 * @param {Object} siteCopy - The site copy object
 * @param {Object} clientCustomLabels - The client custom labels object
 * @param {string} labelSection - The section key (e.g., 'verificationCodeLabels')
 * @param {string} labelKey - The specific label key (e.g., 'submit', 'subtitle')
 * @param {string} fallback - Default fallback value
 * @returns {string} The resolved label value
 */
export const getClientAwareLabel = (
  siteCopy,
  clientCustomLabels,
  labelSection,
  labelKey,
  fallback = '',
) => {
  // First, try to get from client custom labels (brand-specific)
  // Support both 'labelSection:' and 'labelSection' formats
  const sectionLabels =
    clientCustomLabels[`${labelSection}:`] ||
    clientCustomLabels[labelSection] ||
    {};

  if (sectionLabels[labelKey]) {
    return sectionLabels[labelKey];
  }

  // Second, try to get from general site copy labels
  const generalLabels =
    _get(siteCopy, `pageCopy.${labelSection}:`, {}) ||
    _get(siteCopy, `pageCopy.${labelSection}`, {});

  if (generalLabels[labelKey]) {
    return generalLabels[labelKey];
  }

  // Finally, return the fallback
  return fallback;
};

/**
 * Creates a label resolver function for a specific section using client custom labels
 * This works with the useClientCustomLabelsAll hook result
 *
 * @param {Object} siteCopy - The site copy object
 * @param {Object} clientCustomLabels - Result from useClientCustomLabelsAll hook
 * @param {string} labelSection - The section key (e.g., 'verificationCodeLabels')
 * @returns {Function} A function that takes (labelKey, fallback) and returns the resolved label
 */
export const createClientAwareLabelResolver = (
  siteCopy,
  clientCustomLabels,
  labelSection,
) => (labelKey, fallback = '') =>
  getClientAwareLabel(
    siteCopy,
    clientCustomLabels,
    labelSection,
    labelKey,
    fallback,
  );

/**
 * Hook to get all custom labels for the current brand
 * @param {Object} siteCopy - The site copy object containing clientCustomLabels
 * @returns {Object} Object with all custom labels for the brand, or empty object
 */
export const useClientCustomLabelsAll = siteCopy => {
  const brand = getLocalData('brand')?.toLowerCase();

  const customLabels = useMemo(() => {
    const clientCustomLabels = _get(
      siteCopy,
      'pageCopy.clientCustomLabels',
      {},
    );
    const brandLabels = clientCustomLabels[brand] || {};

    // Special handling for blueshield brands - capitalize first word of specific labels
    if (brand && brand.includes('blueshield')) {
      const processedLabels = { ...brandLabels };

      // Capitalize first word for readMoreLabel and readLessLabel
      if (processedLabels.readMoreLabel) {
        processedLabels.readMoreLabel = capitalizeFirstWord(
          processedLabels.readMoreLabel,
        );
      }
      if (processedLabels.readLessLabel) {
        processedLabels.readLessLabel = capitalizeFirstWord(
          processedLabels.readLessLabel,
        );
      }

      return processedLabels;
    }

    return brandLabels;
  }, [siteCopy, brand]);

  return customLabels;
};

export default useClientCustomLabels;
